import React from 'react';
import { ComposedChart, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Bar } from 'recharts';
import { isAfter } from 'date-fns';
import { cloneDeep, isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';

import { OmaLummeApp } from '../../omaLummeContext';

import Button from '../Common/Button';
import Loading from '../Common/Loading';

// Actions.
import { fetchConsumptionRange } from '../../actions';
// Types.
import { GraphTooltipType, ConsumptionRateDatapoint } from '../../types/consumptionRates';
// Utils.
import { setPriceFormat, roundNumber } from '../../utils/billing';
import { capitalizeFirstLetter, getMonthList } from '../../utils/consumption';

const CustomTooltip: React.FunctionComponent<GraphTooltipType> = ({ active, payload, label }: GraphTooltipType) => {
  const { t } = useTranslation();

  if (active && payload && label) {
    const title = payload[0].payload.monthLong;

    return (
      <div className="tooltip-lumme">
        <div className="tooltip-title">{title}</div>
        {payload.map((year) => (
          <div key={year.dataKey}>
            <span>
              {t(`consumption:consumption.reportingGraph-consumption`)} {year.dataKey}
            </span>
            <span>{setPriceFormat(roundNumber(year.payload[parseInt(year.dataKey)])) ?? 0} kWh</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const ConsumptionGraph: React.FunctionComponent = () => {
  const dispatch = OmaLummeApp.useDispatch();
  const { t, i18n } = useTranslation();

  const locale = i18n.language;

  const { users, basicData, selectedMeteringPoint, landingPage } = OmaLummeApp.useState();

  const consumptionRatesSortedInitial: ConsumptionRateDatapoint[] = [];

  const monthsShort = getMonthList(locale, 'short');
  const monthsLong = getMonthList(locale, 'long');

  monthsShort.forEach((month, index) => {
    consumptionRatesSortedInitial.push({
      monthShort: capitalizeFirstLetter(month),
      monthLong: capitalizeFirstLetter(monthsLong[index]),
    });
  });

  const [consumptionRatesSorted, setConsumptionRatesSorted] = React.useState<ConsumptionRateDatapoint[]>(cloneDeep(consumptionRatesSortedInitial));
  const [consumptionRatesFetched, setConsumptionRatesFetched] = React.useState(false);
  const [prevMeteringPoint, setPrevMeteringPoint] = React.useState('');

  const { userId } = users.activeUser;

  const meteringpointContracts = basicData ? basicData.filter((mp) => mp.GSRN === selectedMeteringPoint?.meteringPointId) : undefined;

  const basicContract = meteringpointContracts && meteringpointContracts[0];

  if (basicContract && meteringpointContracts && meteringpointContracts?.length > 1) {
    const firstContract = meteringpointContracts.sort((a, b) => (isAfter(new Date(a.contractStartDate), new Date(b.contractStartDate)) ? 1 : -1))[0];

    basicContract.contractStartDate = firstContract.contractStartDate;
  }

  const startDateInPast = basicContract ? isAfter(new Date(basicContract.contractStartDate), new Date()) : false;

  React.useEffect(() => {
    // Create an scoped async function in the hook
    async function fetchGraphPoints(): Promise<void> {
      setConsumptionRatesFetched(false);
      setConsumptionRatesSorted(cloneDeep(consumptionRatesSortedInitial));

      if (selectedMeteringPoint) {
        const consumptionRates = await fetchConsumptionRange(selectedMeteringPoint.customerId, userId, selectedMeteringPoint.meteringPointId);

        if (consumptionRates?.items.length === 0) {
          setConsumptionRatesFetched(true);
          dispatch(['changeLandingPage', { ...landingPage, currentMonthConsumption: 0, lastYearConsumption: 0 }]);
        }

        const currentDate = new Date();
        let currentConsumption = 0;
        let lastYearConsumption: undefined | number = undefined;
        const consumptionRatesSortedUpdated = cloneDeep(consumptionRatesSortedInitial);
        consumptionRates?.items.forEach((dataPoint) => {
          const month: number = new Date(dataPoint.startTime).getMonth();
          const year = new Date(dataPoint.startTime).getFullYear();

          if (month === currentDate.getMonth() && year === currentDate.getFullYear()) {
            // set the current consumption.
            currentConsumption = dataPoint.sum;
          }
          if (month === currentDate.getMonth() && year === currentDate.getFullYear() - 1 && dataPoint.sum > 0) {
            // set the current consumption.
            lastYearConsumption = dataPoint.sum;
          }

          consumptionRatesSortedUpdated[month][year] = dataPoint.sum;
        });
        if (!isEqual(consumptionRatesSortedUpdated, consumptionRatesSorted)) {
          setConsumptionRatesSorted(consumptionRatesSortedUpdated);
          setConsumptionRatesFetched(true);
          dispatch(['changeLandingPage', { ...landingPage, currentMonthConsumption: currentConsumption, lastYearConsumption: lastYearConsumption }]);
        }
      }
    }
    if (selectedMeteringPoint && selectedMeteringPoint.meteringPointId !== prevMeteringPoint) {
      setPrevMeteringPoint(selectedMeteringPoint.meteringPointId);
      // Execute the created function directly
      fetchGraphPoints();
    }
  }, [consumptionRatesSortedInitial, userId, prevMeteringPoint]);

  React.useEffect(() => {
    if (selectedMeteringPoint && selectedMeteringPoint.meteringPointId === prevMeteringPoint && landingPage.currentMonthConsumption === undefined) {
      consumptionRatesSorted?.forEach((dataPoint, index) => {
        if (index === new Date().getMonth()) {
          dispatch([
            'changeLandingPage',
            {
              ...landingPage,
              currentMonthConsumption: dataPoint[new Date().getFullYear()],
              lastYearConsumption: dataPoint[new Date().getFullYear() - 1],
            },
          ]);
        }
      });
    }
  }, [consumptionRatesSorted, selectedMeteringPoint, prevMeteringPoint]);

  const fillColors: string[] = ['#66921B', '#96008f'];

  const linesToShow: Array<React.JSX.Element> = [];
  let colourKey = 0;
  const years: string[] = [];

  if (consumptionRatesFetched) {
    consumptionRatesSorted.forEach((entry) => {
      for (const year in entry) {
        if (year !== 'monthShort' && year !== 'monthLong' && !years.includes(year)) {
          years.push(year);
          linesToShow.push(<Bar key={year} dataKey={year} fill={fillColors[colourKey]} radius={[50, 50, 0, 0]} />);

          colourKey++;
          if (colourKey >= fillColors.length) {
            colourKey = fillColors.length - 1;
          }
        }
      }
    });
  }

  return (
    <>
      {basicContract && startDateInPast && <p>{t('consumption:consumption.startDateInPast')}</p>}
      {basicContract && !startDateInPast && !consumptionRatesFetched && <Loading />}

      {basicContract && !startDateInPast && consumptionRatesFetched && consumptionRatesSorted && consumptionRatesSorted.length > 0 && (
        <div className="graph-container graph-flex">
          <div className="title">
            <h2>{t('landingPage:graph.consumptionTitle')}</h2>
          </div>
          <div className="graph-wrapper">
            <ResponsiveContainer width={'100%'} height={300}>
              <ComposedChart data={consumptionRatesSorted}>
                <Legend align="right" verticalAlign="top" iconType="circle" />

                <XAxis dataKey="monthShort" stroke="#000000" />
                <YAxis
                  scale="linear"
                  interval="preserveStartEnd"
                  label={{ value: 'kWh', angle: -90, position: 'insideLeft' }}
                  stroke="#000000"
                  strokeWidth={0}
                />

                <Tooltip content={<CustomTooltip />} wrapperStyle={{ width: 300 }} />
                {linesToShow}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <Button className="buttonWhite" link="/consumption" aria-label={t('landingPage:graph.detailsButtonLabel')}>
            {t('landingPage:graph.detailsButton')}
          </Button>
        </div>
      )}
    </>
  );
};

export default ConsumptionGraph;
